import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Student, DayOfWeek, School, LectureLength } from "../types";
import { studentsApi, preferencesApi, lectureLengthsApi } from "../api/client";
import "./StudentList.css";

interface StudentListProps {
  students: Student[];
  schoolId: number;
  schools: School[];
  onStudentsChange: () => void;
  onSelectStudent?: (student: Student) => void;
  selectedStudentId?: number | null;
  canEdit?: boolean;
}

export function StudentList({
  students,
  schoolId,
  schools,
  onStudentsChange,
  onSelectStudent,
  selectedStudentId,
  canEdit = true,
}: StudentListProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [name, setName] = useState("");
  const [studentSchoolId, setStudentSchoolId] = useState<number>(schoolId);
  const [lectureLengths, setLectureLengths] = useState<LectureLength[]>([]);
  const [studentLectureLengthId, setStudentLectureLengthId] = useState<
    number | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefDay, setPrefDay] = useState<DayOfWeek>("monday");
  const [prefStartTime, setPrefStartTime] = useState("09:00");
  const [prefEndTime, setPrefEndTime] = useState("09:30");
  const [prefType, setPrefType] = useState<
    "preferred" | "alternative" | "block_hour"
  >("preferred");
  const [newPrefs, setNewPrefs] = useState<
    {
      day: DayOfWeek;
      start_time: string;
      duration: number;
      type: "preferred" | "alternative" | "block_hour";
    }[]
  >([]);
  const [prefLectureLengthId, setPrefLectureLengthId] = useState<number | "">(
    "",
  );

  const getEndTime = (start: string, durationMin: number) => {
    const hhmm = start.slice(0, 5);
    const [h, m] = hhmm.split(":").map(Number);
    const startTotal = h * 60 + m;
    const endTotal = (startTotal + durationMin) % (24 * 60);
    const eh = Math.floor(endTotal / 60);
    const em = endTotal % 60;
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    return `${pad(eh)}:${pad(em)}`;
  };

  // Auto-derive end time when a lecture length is selected
  useEffect(() => {
    if (prefLectureLengthId) {
      const ll = lectureLengths.find((l) => l.id === prefLectureLengthId);
      if (ll) {
        const [sh, sm] = prefStartTime.split(":").map(Number);
        const startM = sh * 60 + sm;
        const endM = (startM + ll.duration) % (24 * 60);
        const eh = Math.floor(endM / 60),
          em = endM % 60;
        const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
        setPrefEndTime(`${pad(eh)}:${pad(em)}`);
      }
    }
  }, [prefLectureLengthId, prefStartTime, lectureLengths]);

  const handleOpenModal = (student?: Student) => {
    if (student) {
      setEditingStudent(student);
      setName(student.name);
      setStudentSchoolId(student.school_id);
      setStudentLectureLengthId(student.lecture_length_id);
    } else {
      setEditingStudent(null);
      setName("");
      setStudentSchoolId(schoolId);
      setStudentLectureLengthId(undefined);
    }
    setNewPrefs([]);
    setShowModal(true);
    setError(null);
  };
  useEffect(() => {
    // load lecture lengths for selected school in modal
    const load = async () => {
      try {
        const res = await lectureLengthsApi.getBySchool(studentSchoolId);
        setLectureLengths(res.data);
      } catch (e) {
        console.error("Failed to load lecture lengths", e);
      }
    };
    load();
  }, [studentSchoolId, showModal]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingStudent(null);
    setName("");
    setError(null);
    setNewPrefs([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError(t("students.nameRequired"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let targetStudentId: number | null = null;
      if (editingStudent) {
        await studentsApi.update(editingStudent.id, {
          name: name.trim(),
          school_id: studentSchoolId,
          lecture_length_id: studentLectureLengthId,
        });
        targetStudentId = editingStudent.id;
      } else {
        const res = await studentsApi.create({
          name: name.trim(),
          school_id: studentSchoolId,
          lecture_length_id: studentLectureLengthId,
        });
        targetStudentId = res.data.id;
      }

      if (targetStudentId && newPrefs.length > 0) {
        for (const p of newPrefs) {
          await preferencesApi.create({
            student_id: targetStudentId,
            day: p.day,
            start_time: p.start_time,
            duration: p.duration,
            type: p.type,
          });
        }
      }

      await onStudentsChange();
      handleCloseModal();
    } catch (err: any) {
      console.error("Failed to save student:", err);
      const errorMsg =
        err?.response?.data?.detail || err?.message || t("common.error");
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (student: Student) => {
    if (!window.confirm(t("students.confirmDelete", { name: student.name }))) {
      return;
    }

    setLoading(true);
    try {
      await studentsApi.delete(student.id);
      onStudentsChange();
    } catch (err) {
      console.error("Failed to delete student:", err);
      alert(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-list-container">
      <div className="view-header">
        <h2>{t("students.title")}</h2>
        <button
          className="btn-primary"
          onClick={() => handleOpenModal()}
          disabled={!canEdit}
        >
          + {t("students.addStudent")}
        </button>
      </div>

      {students.length === 0 ? (
        <div className="card empty-state">
          <p>{t("students.noStudents")}</p>
          <button
            className="btn-primary"
            onClick={() => handleOpenModal()}
            disabled={!canEdit}
          >
            {t("students.addFirstStudent")}
          </button>
        </div>
      ) : (
        <div className="students-grid">
          {students.map((student) => (
            <div
              key={student.id}
              className={`student-card ${selectedStudentId === student.id ? "selected" : ""}`}
              onClick={() => onSelectStudent && onSelectStudent(student)}
              style={{ cursor: onSelectStudent ? "pointer" : "default" }}
            >
              <div className="student-card-avatar">
                {student.name.charAt(0).toUpperCase()}
              </div>
              <div className="student-card-info">
                <h3>{student.name}</h3>
                <p className="student-card-id">ID: {student.id}</p>
              </div>
              <div className="student-card-actions">
                <button
                  className="btn-icon"
                  onClick={() => handleOpenModal(student)}
                  title={t("common.edit")}
                  disabled={!canEdit}
                >
                  ✏️
                </button>
                <button
                  className="btn-icon btn-icon-danger"
                  onClick={() => handleDelete(student)}
                  title={t("common.delete")}
                  disabled={loading || !canEdit}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingStudent
                  ? t("students.editStudent")
                  : t("students.addStudent")}
              </h3>
              <button className="modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="student-name">{t("students.name")}</label>
                <input
                  id="student-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("students.namePlaceholder")}
                  autoFocus
                  disabled={loading || !canEdit}
                />
              </div>
              <div className="form-group">
                <label htmlFor="student-school">{t("students.school")}</label>
                <select
                  id="student-school"
                  value={studentSchoolId}
                  onChange={(e) => setStudentSchoolId(Number(e.target.value))}
                  disabled={loading || !canEdit}
                >
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              {error && <div className="error-message">{error}</div>}

              {/* Optional initial preferences builder */}
              <div className="pref-builder">
                <div className="pref-builder-header">
                  <h4>{t("preferences.title")}</h4>
                  <p
                    style={{
                      color: "var(--text-tertiary)",
                      fontSize: "0.9rem",
                    }}
                  >
                    {t("schedule.dragToSchedule")}
                  </p>
                </div>
                <div className="pref-form-grid">
                  <div className="form-group">
                    <label htmlFor="pref-day">{t("timeslots.day")}</label>
                    <select
                      id="pref-day"
                      value={prefDay}
                      onChange={(e) => setPrefDay(e.target.value as DayOfWeek)}
                      disabled={loading || !canEdit}
                    >
                      {(
                        [
                          "monday",
                          "tuesday",
                          "wednesday",
                          "thursday",
                          "friday",
                        ] as DayOfWeek[]
                      ).map((d) => (
                        <option key={d} value={d}>
                          {t(`days.${d}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="pref-time">
                      {t("timeslots.startTime")}
                    </label>
                    <input
                      id="pref-time"
                      type="time"
                      value={prefStartTime}
                      onChange={(e) => setPrefStartTime(e.target.value)}
                      disabled={loading || !canEdit}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="pref-end">{t("timeslots.endTime")}</label>
                    <input
                      id="pref-end"
                      type="time"
                      value={prefEndTime}
                      onChange={(e) => setPrefEndTime(e.target.value)}
                      disabled={loading || !canEdit}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="pref-ll">{t("lectureLengths.title")}</label>
                    <select
                      id="pref-ll"
                      value={prefLectureLengthId}
                      onChange={(e) =>
                        setPrefLectureLengthId(
                          e.target.value ? Number(e.target.value) : "",
                        )
                      }
                      disabled={loading || !canEdit}
                    >
                      <option value="">{t("common.none")}</option>
                      {lectureLengths.map((ll) => (
                        <option key={ll.id} value={ll.id}>
                          {ll.name} ({ll.duration} {t("lectureLengths.minutes")}
                          )
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="pref-type">
                      {t("preferences.preferenceType")}
                    </label>
                    <select
                      id="pref-type"
                      value={prefType}
                      onChange={(e) =>
                        setPrefType(
                          e.target.value as
                            | "preferred"
                            | "alternative"
                            | "block_hour",
                        )
                      }
                      disabled={loading || !canEdit}
                    >
                      <option value="preferred">
                        ⭐ {t("preferences.preferred")}
                      </option>
                      <option value="alternative">
                        ⚠️ {t("preferences.alternative")}
                      </option>
                      <option value="block_hour">
                        🚫 {t("preferences.blockHour")}
                      </option>
                    </select>
                  </div>
                </div>
                <div className="pref-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setNewPrefs([])}
                    disabled={loading || newPrefs.length === 0 || !canEdit}
                  >
                    {t("common.clear")}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                      let duration: number;
                      if (prefLectureLengthId) {
                        const ll = lectureLengths.find(
                          (l) => l.id === prefLectureLengthId,
                        );
                        duration = ll ? ll.duration : 0;
                      } else {
                        const [sh, sm] = prefStartTime.split(":").map(Number);
                        const [eh, em] = prefEndTime.split(":").map(Number);
                        const startM = sh * 60 + sm;
                        const endM = eh * 60 + em;
                        duration = endM - startM;
                      }
                      if (duration <= 0) {
                        alert(t("timeslots.timeRangeInvalid"));
                        return;
                      }
                      setNewPrefs((prev) => [
                        ...prev,
                        {
                          day: prefDay,
                          start_time: prefStartTime,
                          duration,
                          type: prefType,
                        },
                      ]);
                    }}
                    disabled={loading || !canEdit}
                  >
                    + {t("preferences.addPreference")}
                  </button>
                </div>

                {newPrefs.length > 0 && (
                  <div className="pref-list">
                    {newPrefs.map((p, idx) => (
                      <div key={idx} className={`preference-item ${p.type}`}>
                        <span className="preference-type">
                          {p.type === "preferred"
                            ? "⭐"
                            : p.type === "alternative"
                              ? "⚠️"
                              : "🚫"}
                        </span>
                        <span className="preference-day">
                          {t(`days.${p.day}`)}
                        </span>
                        <span className="preference-time">
                          {p.start_time}–{getEndTime(p.start_time, p.duration)}
                        </span>
                        <button
                          type="button"
                          className="btn-icon btn-icon-danger"
                          onClick={() =>
                            setNewPrefs((prev) =>
                              prev.filter((_, i) => i !== idx),
                            )
                          }
                          title={t("common.delete")}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseModal}
                  disabled={loading}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading || !canEdit}
                >
                  {loading ? t("common.saving") : t("common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
